import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabase/config';
import { useAuth } from './AuthContext';
import {
  fetchLivePositions, fetchLiveTrades, syncLiveConfig, updateLiveStrategy,
  fetchUserSettings, fetchWatchlist, addWatchlistItem, removeWatchlistItem,
} from '../supabase/liveData';

const BotContext = createContext();
export const useBot = () => useContext(BotContext);

export const BotProvider = ({ children }) => {
  const { user } = useAuth();
  // --- Core State ---
  const [balance, setBalance] = useState(() => {
    const saved = localStorage.getItem('bot_balance');
    return saved ? parseFloat(saved) : 100.0;
  });
  const [positions, setPositions] = useState(() => {
    const saved = localStorage.getItem('bot_positions');
    return saved ? JSON.parse(saved) : [];
  });
  const [closedTrades, setClosedTrades] = useState(() => {
    const saved = localStorage.getItem('bot_closed_trades');
    return saved ? JSON.parse(saved) : [];
  });
  const [watchlist, setWatchlist] = useState(() => {
    const saved = localStorage.getItem('bot_watchlist');
    return saved ? JSON.parse(saved) : [];
  });
  const [activityLog, setActivityLog] = useState(() => {
    const saved = localStorage.getItem('bot_activity_log');
    return saved ? JSON.parse(saved) : [];
  });
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('bot_settings');
    const parsed = saved ? JSON.parse(saved) : {};
    return {
      hardStopLoss: parsed.hardStopLoss ?? 15,
      trailingStopLoss: parsed.trailingStopLoss ?? 30,
      trailingActivationMultiplier: parsed.trailingActivationMultiplier ?? 1.5,
      hardStopEnabled: parsed.hardStopEnabled ?? true,
      trailingStopEnabled: parsed.trailingStopEnabled ?? true,
      profitAlerts: parsed.profitAlerts ?? [50, 100, 200, 500],
      lossAlerts: parsed.lossAlerts ?? [10, 20, 30],
      watchlistAlerts: parsed.watchlistAlerts ?? [10, 25, 50],
      takeProfitEnabled: parsed.takeProfitEnabled ?? true,
      takeProfitTiers: parsed.takeProfitTiers ?? [
        { at: 50, sellPct: 25 },
        { at: 100, sellPct: 25 },
        { at: 200, sellPct: 50 },
      ],
      heliusEnabled: parsed.heliusEnabled ?? false,
      birdeyeEnabled: parsed.birdeyeEnabled ?? false,
      notificationsEnabled: parsed.notificationsEnabled ?? true,
      tradingMode: parsed.tradingMode ?? 'paper',
    };
  });

  const [notifications, setNotifications] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  // --- Live (real on-chain) data from Supabase, managed by the backend bot ---
  const [livePositions, setLivePositions] = useState([]);
  const [liveTrades, setLiveTrades] = useState([]);

  useEffect(() => {
    if (!user?.id) { setLivePositions([]); setLiveTrades([]); return; }
    let active = true;
    const reload = async () => {
      const [pos, trades] = await Promise.all([fetchLivePositions(user.id), fetchLiveTrades(user.id)]);
      if (active) { setLivePositions(pos); setLiveTrades(trades); }
    };
    reload();
    const channel = supabase
      .channel(`live:${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'live_positions', filter: `user_id=eq.${user.id}` }, reload)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'live_trades', filter: `user_id=eq.${user.id}` }, reload)
      .subscribe();
    return () => { active = false; supabase.removeChannel(channel); };
  }, [user?.id]);

  // Hydrate settings + watchlist from Supabase on login so they follow the user
  // across machines (localStorage is only a same-device cache / offline fallback).
  useEffect(() => {
    if (!user?.id) { settingsHydratedRef.current = false; return; }
    let active = true;
    (async () => {
      // Settings
      try {
        const remote = await fetchUserSettings(user.id);
        if (active && remote) {
          setSettings(prev => ({ ...prev, ...remote }));
        } else if (active) {
          // No saved settings yet — seed the DB from this device's current settings.
          syncLiveConfig(user.id, settingsRef.current).catch(() => {});
        }
      } catch (_e) { /* keep localStorage settings */ }
      if (active) settingsHydratedRef.current = true;

      // Watchlist: prefer the DB; if empty but this device has a local list, migrate it up.
      try {
        let remoteWl = await fetchWatchlist(user.id);
        if (active && remoteWl.length === 0 && watchlistRef.current.length > 0) {
          const migrated = [];
          for (const w of watchlistRef.current) {
            try {
              const row = await addWatchlistItem(user.id, {
                symbol: w.symbol, address: w.address,
                initialPrice: w.initialPrice ?? w.priceNative ?? 0,
                initialPriceUsd: w.initialPriceUsd ?? null,
              });
              if (row) migrated.push(row);
            } catch (_e) { /* skip dupes/failures */ }
          }
          remoteWl = migrated.length ? migrated : await fetchWatchlist(user.id);
        }
        if (active) setWatchlist(remoteWl);
      } catch (_e) { /* keep localStorage watchlist */ }
    })();
    return () => { active = false; };
  }, [user?.id]);

  // Keep the backend engine's rules + the cross-device settings in sync.
  // Gated on hydration so we never overwrite saved settings with this device's defaults.
  useEffect(() => {
    if (!user?.id || !settingsHydratedRef.current) return;
    syncLiveConfig(user.id, settings).catch(() => {});
  }, [user?.id, settings]);

  // Refs for interval access
  const positionsRef = useRef(positions);
  const watchlistRef = useRef(watchlist);
  const settingsRef = useRef(settings);
  const settingsHydratedRef = useRef(false);
  const refreshTimeoutRef = useRef(null);
  const scheduleRef = useRef(null);

  // --- Persistence ---
  useEffect(() => {
    positionsRef.current = positions;
    localStorage.setItem('bot_positions', JSON.stringify(positions));
  }, [positions]);

  useEffect(() => {
    watchlistRef.current = watchlist;
    localStorage.setItem('bot_watchlist', JSON.stringify(watchlist));
  }, [watchlist]);

  useEffect(() => localStorage.setItem('bot_balance', balance.toString()), [balance]);
  useEffect(() => localStorage.setItem('bot_closed_trades', JSON.stringify(closedTrades)), [closedTrades]);
  useEffect(() => localStorage.setItem('bot_activity_log', JSON.stringify(activityLog)), [activityLog]);
  useEffect(() => {
    settingsRef.current = settings;
    localStorage.setItem('bot_settings', JSON.stringify(settings));
  }, [settings]);

  // --- Helpers ---
  const notify = useCallback((message, type = 'info') => {
    // Respect the global notifications toggle (read via ref so notify stays stable).
    // The activity log still records everything, so muting only hides the toasts.
    if (settingsRef.current?.notificationsEnabled === false) return;
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  }, []);

  const logActivity = useCallback((title, description, type = 'info') => {
    setActivityLog(prev => {
      const newLog = [{ id: Date.now().toString() + Math.random(), title, description, type, timestamp: new Date().toISOString() }, ...prev];
      return newLog.slice(0, 50);
    });
  }, []);

  const resetAccount = useCallback(() => {
    setBalance(100.0);
    setPositions([]);
    setClosedTrades([]);
    setActivityLog([]);
    setWatchlist([]);
    logActivity('Account Reset', 'Paper trading account has been reset to 100 SOL.', 'info');
    notify('Account has been reset', 'success');
  }, [logActivity, notify]);

  // --- Core Engine ---
  const refreshPrices = useCallback(async () => {
    const currentPositions = positionsRef.current;
    const currentWatchlist = watchlistRef.current;
    if (currentPositions.length === 0 && currentWatchlist.length === 0) return;

    setIsRefreshing(true);
    const posAddresses = currentPositions.map(p => p.address);
    const watchAddresses = currentWatchlist.map(w => w.address);
    const addressesToFetch = [...new Set([...posAddresses, ...watchAddresses])].slice(0, 30).join(',');

    try {
      const res = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${addressesToFetch}`);
      const data = await res.json();
      const pairMap = {};

      if (data.pairs) {
        const validPairs = data.pairs.filter(p => {
          const quoteSymbol = p.quoteToken?.symbol?.toUpperCase();
          return p.chainId === 'solana' && p.priceNative && (quoteSymbol === 'SOL' || quoteSymbol === 'WSOL');
        });
        validPairs.sort((a, b) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0));
        validPairs.forEach(pair => {
          if (!pairMap[pair.baseToken.address]) {
            pairMap[pair.baseToken.address] = pair;
          }
        });
      }

      // Positions Update
      const newlyClosed = [];
      let totalReturnedSol = 0;
      let positionsUpdated = false;

      const newPositions = currentPositions.map(pos => {
        const pairData = pairMap[pos.address];
        if (!pairData) return pos;

        const newPrice = parseFloat(pairData.priceNative);
        const newPeak = Math.max(pos.peakPrice, newPrice);
        const newPriceUsd = parseFloat(pairData.priceUsd || '0') || 0;
        const newPeakUsd = Math.max(pos.peakPriceUsd || 0, newPriceUsd);
        let shouldSell = false;
        let sellReason = '';

        const pnlPercentage = ((newPrice - pos.buyPrice) / pos.buyPrice) * 100;
        const currentMilestones = pos.milestonesHit || [];
        let newMilestones = [...currentMilestones];
        let triggeredAlert = false;

        (settings.profitAlerts || []).forEach(target => {
          if (pnlPercentage >= target && !newMilestones.includes(target)) {
            newMilestones.push(target);
            triggeredAlert = true;
            notify(`🚀 Profit Alert: $${pos.symbol} is up +${pnlPercentage.toFixed(1)}%! Passed ${target}% target.`, 'success');
            logActivity('Profit Target Hit', `$${pos.symbol} exceeded +${target}% profit.`, 'success');
          }
        });

        const currentLossMilestones = pos.lossMilestonesHit || [];
        let newLossMilestones = [...currentLossMilestones];
        (settings.lossAlerts || []).forEach(target => {
          if (pnlPercentage <= -target && !newLossMilestones.includes(target)) {
            newLossMilestones.push(target);
            triggeredAlert = true;
            notify(`⚠️ Loss Alert: $${pos.symbol} is down ${pnlPercentage.toFixed(1)}%! Passed -${target}% threshold.`, 'error');
            logActivity('Loss Threshold Hit', `$${pos.symbol} dropped below -${target}%.`, 'error');
          }
        });

        // Auto Take-Profit: accumulate which tiers to execute this tick
        let newTpHit = [...(pos.takeProfitHit || [])];
        let tpSellPct = 0;
        if (settings.takeProfitEnabled && settings.takeProfitTiers) {
          for (const tier of settings.takeProfitTiers) {
            if (tier.enabled === false) continue;
            if (pnlPercentage >= tier.at && !newTpHit.includes(tier.at)) {
              newTpHit.push(tier.at);
              tpSellPct += tier.sellPct;
            }
          }
        }

        // Determine Effective Settings (Custom vs Global)
        const pnlDropFromBuy = (pos.buyPrice - newPrice) / pos.buyPrice;
        const effectiveHardSL = pos.customHardSL ?? settings.hardStopLoss;
        const effectiveTrailingSL = pos.customTrailingSL ?? settings.trailingStopLoss;
        const effectiveTrailingAct = pos.customTrailingAct ?? settings.trailingActivationMultiplier;

        // REAL-TIME STRATEGY ASSESSMENT
        let liveStrategy = { action: 'HOLD', color: 'text-gray-400', bg: 'bg-gray-500/10', border: 'border-gray-500/30', icon: 'FiActivity', text: 'Monitoring price action.' };
        const isTrailingActive = newPeak >= pos.buyPrice * effectiveTrailingAct;
        const dropFromPeak = newPeak > 0 ? ((newPeak - newPrice) / newPeak) * 100 : 0;

        if (pnlPercentage <= -effectiveHardSL * 0.8) {
          liveStrategy = { action: 'CRITICAL RISK', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: 'FiAlertTriangle', text: `Approaching hard stop loss of -${effectiveHardSL}%. Prepare for auto-sell.` };
        } else if (isTrailingActive) {
          if (dropFromPeak >= effectiveTrailingSL * 0.8) {
            liveStrategy = { action: 'TRAIL WARNING', color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', icon: 'FiShield', text: `Price dropping from peak. Trailing stop at -${effectiveTrailingSL}% is near.` };
          } else {
            liveStrategy = { action: 'RIDING TREND', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: 'FiTrendingUp', text: `Trailing stop active. Locked in profits, riding the trend upwards.` };
          }
        } else if (pnlPercentage >= (effectiveTrailingAct - 1) * 100 * 0.8) {
          liveStrategy = { action: 'PROFITABLE', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30', icon: 'FiCheckCircle', text: `Strong profit margin. Waiting for trailing stop activation at ${effectiveTrailingAct}x.` };
        } else if (pnlPercentage > 0) {
          liveStrategy = { action: 'BULLISH', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30', icon: 'FiTrendingUp', text: `Position is in profit. Consolidating gains.` };
        } else {
          liveStrategy = { action: 'BEARISH', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', icon: 'FiTrendingDown', text: `Position is underwater. Monitoring for hard stop loss.` };
        }

        // 1. Hard Stop Loss (skipped when globally disabled)
        if (settings.hardStopEnabled !== false && pnlDropFromBuy >= (effectiveHardSL / 100)) {
          shouldSell = true;
          sellReason = `Hard SL Hit (-${(pnlDropFromBuy * 100).toFixed(1)}%)`;
        }

        // 2. Trailing Stop Loss (dropFromPeak and effectiveTrailingSL are both %; skipped when disabled)
        if (!shouldSell && settings.trailingStopEnabled !== false && isTrailingActive) {
          if (dropFromPeak >= effectiveTrailingSL) {
            shouldSell = true;
            sellReason = `Trailing SL Hit (-${dropFromPeak.toFixed(1)}% from peak)`;
          }
        }

        if (shouldSell) {
          const returnedSol = pos.amountSol * (newPrice / pos.buyPrice);
          totalReturnedSol += returnedSol;
          notify(`Auto-Sold $${pos.symbol}: ${sellReason}`, 'error');
          logActivity('Auto-Sell Executed', `Sold $${pos.symbol} due to: ${sellReason}. Returned ${returnedSol.toFixed(3)} SOL.`, 'error');
          positionsUpdated = true;

          newlyClosed.push({
            id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
            symbol: pos.symbol,
            address: pos.address,
            buyPrice: pos.buyPrice,
            sellPrice: newPrice,
            buyPriceUsd: pos.buyPriceUsd ?? null,
            sellPriceUsd: newPriceUsd || null,
            pnlPercentage: pnlPercentage,
            pnlSol: returnedSol - pos.amountSol,
            reason: sellReason,
            closedAt: new Date().toISOString()
          });
          return null;
        }

        // Auto Take-Profit Execution (partial sell)
        if (tpSellPct > 0) {
          const actualSellPct = Math.min(tpSellPct, 100);
          const remainingPct = 1 - actualSellPct / 100;
          const soldAmountSol = pos.amountSol * (actualSellPct / 100);
          const returnedSol = soldAmountSol * (newPrice / pos.buyPrice);
          const pnlSolPartial = returnedSol - soldAmountSol;
          const newlyHitTiers = newTpHit.filter(t => !(pos.takeProfitHit || []).includes(t));
          const topTier = Math.max(...newlyHitTiers);

          totalReturnedSol += returnedSol;
          positionsUpdated = true;

          notify(`🎯 Auto TP: Sold ${actualSellPct}% of $${pos.symbol} at +${pnlPercentage.toFixed(1)}%`, 'success');
          logActivity('Auto Take-Profit', `Sold ${actualSellPct}% of $${pos.symbol} at +${topTier}% tier. +${pnlSolPartial.toFixed(3)} SOL.`, 'success');

          newlyClosed.push({
            id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
            symbol: pos.symbol,
            address: pos.address,
            buyPrice: pos.buyPrice,
            sellPrice: newPrice,
            buyPriceUsd: pos.buyPriceUsd ?? null,
            sellPriceUsd: newPriceUsd || null,
            pnlPercentage,
            pnlSol: pnlSolPartial,
            reason: `Auto TP (+${topTier}%)`,
            closedAt: new Date().toISOString()
          });

          if (remainingPct <= 0.01) return null;

          return {
            ...pos,
            currentPrice: newPrice,
            peakPrice: newPeak,
            currentPriceUsd: newPriceUsd,
            peakPriceUsd: newPeakUsd,
            amountSol: pos.amountSol * remainingPct,
            tokens: pos.tokens * remainingPct,
            milestonesHit: newMilestones,
            lossMilestonesHit: newLossMilestones,
            takeProfitHit: newTpHit,
            liquidityQuoteSol: pairData.liquidity?.quote || pos.liquidityQuoteSol || 0,
            realTimeStrategy: liveStrategy
          };
        }

        if (newPrice !== pos.currentPrice || newPeak !== pos.peakPrice || triggeredAlert || pos.realTimeStrategy?.action !== liveStrategy.action) {
          positionsUpdated = true;
          return {
            ...pos,
            currentPrice: newPrice,
            peakPrice: newPeak,
            currentPriceUsd: newPriceUsd,
            peakPriceUsd: newPeakUsd,
            milestonesHit: newMilestones,
            lossMilestonesHit: newLossMilestones,
            takeProfitHit: newTpHit,
            liquidityQuoteSol: pairData.liquidity?.quote || pos.liquidityQuoteSol || 0,
            realTimeStrategy: liveStrategy
          };
        }
        return pos;
      }).filter(Boolean);

      if (positionsUpdated) setPositions(newPositions);
      if (newlyClosed.length > 0) setClosedTrades(prev => [...newlyClosed, ...prev]);
      if (totalReturnedSol > 0) setBalance(prev => prev + totalReturnedSol);

      // Watchlist Update
      let watchlistUpdated = false;
      const newWatchlist = currentWatchlist.map(w => {
        const pairData = pairMap[w.address];
        if (!pairData) return w;

        const newPrice = parseFloat(pairData.priceNative);
        const newPriceUsd = parseFloat(pairData.priceUsd || '0') || null;
        const newChange = pairData.priceChange?.h24 || 0;
        const initial = w.initialPrice || w.priceNative;
        const current = w.currentPrice || w.priceNative;
        const pnlSinceAdd = ((newPrice - initial) / initial) * 100;
        
        let newMilestonesUp = [...(w.milestonesHitUp || [])];
        let newMilestonesDown = [...(w.milestonesHitDown || [])];
        let triggeredAlert = false;

        (settings.watchlistAlerts || []).forEach(target => {
          if (pnlSinceAdd >= target && !newMilestonesUp.includes(target)) {
            newMilestonesUp.push(target);
            triggeredAlert = true;
            notify(`👀 Watchlist: $${w.symbol} is UP +${pnlSinceAdd.toFixed(1)}% since tracking!`, 'success');
          }
          if (pnlSinceAdd <= -target && !newMilestonesDown.includes(target)) {
            newMilestonesDown.push(target);
            triggeredAlert = true;
            notify(`👀 Watchlist: $${w.symbol} is DOWN ${pnlSinceAdd.toFixed(1)}% since tracking.`, 'error');
          }
        });

        if (newPrice !== current || newChange !== w.priceChange24h || triggeredAlert) {
          watchlistUpdated = true;
          return { ...w, initialPrice: initial, currentPrice: newPrice, currentPriceUsd: newPriceUsd, priceChange24h: newChange, milestonesHitUp: newMilestonesUp, milestonesHitDown: newMilestonesDown };
        }
        return w;
      });

      if (watchlistUpdated) setWatchlist(newWatchlist);
      setLastRefreshed(new Date());

    } catch (error) {
      console.error("Failed to fetch live prices:", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [settings, notify, logActivity]);

  // Manual refresh for LIVE positions/trades. `refreshPrices` only touches paper
  // positions, so in live mode the Positions refresh button must re-fetch the
  // latest engine-written rows from Supabase (Realtime keeps them fresh too, but
  // the button should also work on demand). Mirrors refreshPrices' UI state.
  const refreshLiveData = useCallback(async () => {
    if (!user?.id) return;
    setIsRefreshing(true);
    try {
      const [pos, trades] = await Promise.all([fetchLivePositions(user.id), fetchLiveTrades(user.id)]);
      setLivePositions(pos);
      setLiveTrades(trades);
      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Failed to refresh live data:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    const schedule = async () => {
      if (cancelled) return;
      await refreshPrices();
      if (cancelled) return;
      const currentPositions = positionsRef.current;
      const closestRisk = currentPositions.reduce((max, pos) => {
        const drop = pos.buyPrice > 0 ? (pos.buyPrice - pos.currentPrice) / pos.buyPrice * 100 : 0;
        return Math.max(max, drop);
      }, 0);
      const delay = closestRisk > 15 ? 3000 : currentPositions.length > 0 ? 5000 : 10000;
      refreshTimeoutRef.current = setTimeout(schedule, delay);
      scheduleRef.current = schedule;
    };
    scheduleRef.current = schedule;
    schedule();
    return () => {
      cancelled = true;
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, [refreshPrices]);

  // --- Helius WebSocket (event-driven refresh trigger for positions only) ---
  const positionAddressKey = positions.map(p => p.address).sort().join(',');
  useEffect(() => {
    if (!settings.heliusEnabled) return;
    const apiKey = import.meta.env.VITE_HELIUS_API_KEY;
    if (!apiKey) {
      notify('Helius API key not set in .env', 'error');
      return;
    }
    const addresses = [...new Set(positionsRef.current.map(p => p.address))];
    if (addresses.length === 0) return;

    const ws = new WebSocket(`wss://beta.helius-rpc.com/?api-key=${apiKey}`);
    let debounceTimer = null;

    ws.onopen = () => {
      addresses.forEach((address, i) => {
        ws.send(JSON.stringify({
          jsonrpc: '2.0',
          id: i + 1,
          method: 'logsSubscribe',
          params: [{ mentions: [address] }, { commitment: 'confirmed' }],
        }));
      });
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.method !== 'logsNotification') return;
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          clearTimeout(refreshTimeoutRef.current);
          scheduleRef.current?.();
        }, 500);
      } catch (_) { /* ignore parse errors */ }
    };

    ws.onerror = () => notify('Helius WebSocket error', 'error');

    return () => {
      clearTimeout(debounceTimer);
      ws.close();
    };
  }, [settings.heliusEnabled, positionAddressKey]);

  // --- User Actions ---
  const addPosition = (symbol, address, initialPriceNative, amountSol, liquidityUsd = 0, liquidityQuoteSol = 0, initialPriceUsd = '0', isReal = false, tokenDecimals = null) => {
    const amount = parseFloat(amountSol);
    if (balance < amount) {
      notify('Insufficient simulated balance!', 'error');
      return;
    }

    setBalance(prev => prev - amount);
    const newPosition = {
      id: Date.now().toString(),
      symbol,
      address,
      buyPrice: parseFloat(initialPriceNative),
      currentPrice: parseFloat(initialPriceNative),
      peakPrice: parseFloat(initialPriceNative),
      amountSol: amount,
      tokens: amount / parseFloat(initialPriceNative),
      milestonesHit: [],
      lossMilestonesHit: [],
      takeProfitHit: [],
      liquidityUsd,
      liquidityQuoteSol,
      buyPriceUsd: parseFloat(initialPriceUsd) || 0,
      currentPriceUsd: parseFloat(initialPriceUsd) || 0,
      peakPriceUsd: parseFloat(initialPriceUsd) || 0,
      customHardSL: null,
      customTrailingSL: null,
      customTrailingAct: null,
      isReal,
      tokenDecimals,
      realTimeStrategy: { 
        action: 'NEW POSITION', 
        color: 'text-blue-400', 
        bg: 'bg-blue-500/10', 
        border: 'border-blue-500/30', 
        icon: 'FiActivity', 
        text: 'Position opened. Analyzing initial price action...' 
      }
    };
    setPositions([newPosition, ...positions]);
    
    const formattedPrice = parseFloat(initialPriceNative).toLocaleString('en-US', { maximumSignificantDigits: 6 });
    notify(`${isReal ? 'Live' : 'Paper'} Bought $${symbol} at ${formattedPrice} SOL`, 'success');
    logActivity('Position Opened', `${isReal ? '[LIVE]' : '[PAPER]'} Bought ${amount} SOL worth of $${symbol} at ${formattedPrice} SOL.`, 'success');
    setLastRefreshed(new Date());
  };

  const sellPosition = (id, percentage) => {
    const currentPositions = positionsRef.current;
    const pos = currentPositions.find(p => p.id === id);
    if (!pos) return;

    const remainingPercentage = 1 - (percentage / 100);
    const soldAmountSol = pos.amountSol * (percentage / 100);
    const sellPrice = pos.currentPrice;
    
    const pnlPercentage = ((sellPrice - pos.buyPrice) / pos.buyPrice) * 100;
    const returnedSol = soldAmountSol * (sellPrice / pos.buyPrice);
    const pnlSol = returnedSol - soldAmountSol;

    setBalance(prev => prev + returnedSol);
    notify(`Paper Sold ${percentage}% of $${pos.symbol}.`, 'info');
    logActivity('Position Sold', `Sold ${percentage}% of $${pos.symbol}. PnL: ${pnlSol > 0 ? '+' : ''}${pnlSol.toFixed(4)} SOL (${pnlPercentage.toFixed(2)}%).`, pnlSol >= 0 ? 'success' : 'error');

    const closedRecord = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
      symbol: pos.symbol,
      address: pos.address,
      buyPrice: pos.buyPrice,
      sellPrice: sellPrice,
      buyPriceUsd: pos.buyPriceUsd ?? null,
      sellPriceUsd: pos.currentPriceUsd ?? null,
      pnlPercentage,
      pnlSol,
      reason: `Manual Sell (${percentage}%)`,
      closedAt: new Date().toISOString()
    };

    setClosedTrades(prev => [closedRecord, ...prev]);

    if (remainingPercentage <= 0.01) {
      setPositions(prev => prev.filter(p => p.id !== id));
    } else {
      setPositions(prev => prev.map(p => {
        if (p.id === id) {
          return { ...p, amountSol: p.amountSol * remainingPercentage, tokens: p.tokens * remainingPercentage };
        }
        return p;
      }));
    }
  };

  const sellMultiplePositions = (ids, percentage) => {
    const currentPositions = positionsRef.current;
    const posToSell = currentPositions.filter(p => ids.includes(p.id));
    if (posToSell.length === 0) return;

    const remainingPercentage = 1 - (percentage / 100);
    const newClosedRecords = [];
    let totalReturnedSol = 0;

    posToSell.forEach(pos => {
      const soldAmountSol = pos.amountSol * (percentage / 100);
      const sellPrice = pos.currentPrice;
      const returnedSol = soldAmountSol * (sellPrice / pos.buyPrice);
      const pnlPercentage = ((sellPrice - pos.buyPrice) / pos.buyPrice) * 100;
      const pnlSol = returnedSol - soldAmountSol;

      totalReturnedSol += returnedSol;

      newClosedRecords.push({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
        symbol: pos.symbol,
        address: pos.address,
        buyPrice: pos.buyPrice,
        sellPrice: sellPrice,
        buyPriceUsd: pos.buyPriceUsd ?? null,
        sellPriceUsd: pos.currentPriceUsd ?? null,
        pnlPercentage,
        pnlSol,
        reason: `Bulk Sell (${percentage}%)`,
        closedAt: new Date().toISOString()
      });
    });

    setBalance(prev => prev + totalReturnedSol);
    setClosedTrades(prev => [...newClosedRecords, ...prev]);

    if (remainingPercentage <= 0.01) {
      setPositions(prev => prev.filter(p => !ids.includes(p.id)));
    } else {
      setPositions(prev => prev.map(p => {
        if (ids.includes(p.id)) {
          return { ...p, amountSol: p.amountSol * remainingPercentage, tokens: p.tokens * remainingPercentage };
        }
        return p;
      }));
    }

    notify(`Bulk Sold ${percentage}% of ${posToSell.length} position(s).`, 'info');
    logActivity('Bulk Sell Executed', `Sold ${percentage}% of ${posToSell.length} positions. Returned ${totalReturnedSol.toFixed(3)} SOL.`, 'info');
  };

  const updatePositionSettings = (id, newSettings) => {
    setPositions(prev => prev.map(p => {
      if (p.id === id) {
        return { ...p, ...newSettings };
      }
      return p;
    }));
    notify('Custom strategy applied', 'success');
  };

  // Live equivalent: persist the override to Supabase so the backend engine uses it.
  // The Realtime subscription on live_positions reloads the UI (badge + values).
  const updateLivePositionStrategy = useCallback(async (id, newSettings) => {
    try {
      await updateLiveStrategy(id, newSettings);
      notify('Custom strategy applied', 'success');
    } catch (e) {
      notify(`Failed to save strategy: ${e.message}`, 'error');
    }
  }, [notify]);

  const addTokenToWatchlist = async (symbol, address, priceNative, priceUsd = null) => {
    if (watchlist.some(w => w.address === address)) {
      notify(`$${symbol} is already in your watchlist.`, 'info');
      return;
    }
    const initialPrice = parseFloat(priceNative);
    const initialPriceUsd = priceUsd ? parseFloat(priceUsd) : null;

    let newItem = {
      id: Date.now().toString(),
      symbol,
      address,
      initialPrice,
      currentPrice: initialPrice,
      priceNative: initialPrice,
      initialPriceUsd,
      currentPriceUsd: initialPriceUsd,
      priceChange24h: 0,
      addedAt: new Date().toISOString(),
      milestonesHitUp: [],
      milestonesHitDown: []
    };

    // Persist to Supabase so the watchlist follows the user across machines.
    if (user?.id) {
      try {
        const row = await addWatchlistItem(user.id, { symbol, address, initialPrice, initialPriceUsd });
        if (!row) { notify(`$${symbol} is already in your watchlist.`, 'info'); return; }
        newItem = row;
      } catch (e) {
        notify(`Failed to save to watchlist: ${e.message}`, 'error');
        return;
      }
    }

    setWatchlist(prev => [newItem, ...prev]);
    notify(`Added $${symbol} to Watchlist`, 'success');
    refreshPrices();
  };

  const removeTokenFromWatchlist = async (id) => {
    setWatchlist(prev => prev.filter(w => w.id !== id));
    notify('Removed from Watchlist', 'info');
    if (user?.id) {
      try { await removeWatchlistItem(id); }
      catch (e) { notify(`Failed to remove from watchlist: ${e.message}`, 'error'); }
    }
  };

  return (
    <BotContext.Provider value={{
      balance, positions, closedTrades, watchlist, activityLog, settings, notifications,
      isRefreshing, lastRefreshed, livePositions, liveTrades,
      refreshPrices, refreshLiveData, addPosition, sellPosition, sellMultiplePositions, updatePositionSettings, updateLivePositionStrategy,
      addTokenToWatchlist, removeTokenFromWatchlist, setSettings, notify, resetAccount
    }}>
      {children}
    </BotContext.Provider>
  );
};